import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CreatePalletLabelHTMLFromBase64,
  CreatePalletLabelHTMLFromCacheId,
} from "../components/printing/generate-pallet-label-html";
import { useDebugMode } from "../hooks/useDebugMode";

export interface PalletLabelGeneratorPageProps {
  autoPrint?: boolean;
}

const PalletLabelGeneratorPage = ({
  autoPrint,
}: PalletLabelGeneratorPageProps) => {
  const location = useLocation();
  const [labelHTML, setLabelHTML] = useState<string>("");
  const [error, setError] = useState<string>();

  const [hasShownPrint, setHasShownPrint] = useState<boolean>(false);
  const debugMode = useDebugMode();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    // Check if we have a cache ID
    const cacheId = queryParams.get("cacheId");
    if (cacheId) {
      CreatePalletLabelHTMLFromCacheId(cacheId, debugMode)
        .then((html) => {
          setLabelHTML(html);
          if (autoPrint) {
            setTimeout(() => {
              if (hasShownPrint) return;
              window.print();
              setHasShownPrint(true);
            }, 1000);
          }
        })
        .catch((error) => {
          console.error("Error generating label HTML from cache:", error);
          setError("Error generating label HTML from cache.");
        });
      return;
    }

    // Fallback to original base64 method
    let encoded = false;
    let base64Data = queryParams.get("data");

    if (!base64Data) {
      if (queryParams.has("dataEncoded")) {
        base64Data = queryParams.get("dataEncoded");
        encoded = true;
      } else {
        setError("No data provided in query string.");
        return;
      }
    }

    let processedBase64Data;
    if (encoded) {
      try {
        const jsonString = atob(base64Data!);
        const data = JSON.parse(decodeURIComponent(jsonString));
        processedBase64Data = btoa(JSON.stringify(data));
      } catch (e) {
        setError("Invalid encoded data format.");
        return;
      }
    } else {
      processedBase64Data = base64Data!;
    }

    CreatePalletLabelHTMLFromBase64(processedBase64Data, debugMode)
      .then((html) => {
        setLabelHTML(html);
        if (autoPrint) {
          setTimeout(() => {
            if (hasShownPrint) return;
            window.print();
            setHasShownPrint(true);
          }, 1000);
        }
      })
      .catch((error) => {
        console.error("Error generating label HTML:", error);
        setError("Error generating label HTML.");
      });
  }, [location.search, autoPrint, hasShownPrint, debugMode]);

  return (
    <>
      {error ? (
        <>
          <h5>Error</h5>
          <p>{error}</p>
        </>
      ) : null}
      <div dangerouslySetInnerHTML={{ __html: labelHTML }} />
    </>
  );
};

export default PalletLabelGeneratorPage;
